import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, Boxes, Cpu } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CodeBox, DemoHero, Callout, useDocMeta } from '../components/esl/EslShared';

const AssetPill = ({ src, label }) => (
  <div className="group flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-800/40 border border-slate-700/60 hover:border-cyan-500/40 transition-colors">
    <div className="w-16 h-16 flex items-center justify-center bg-slate-950/60 rounded-lg overflow-hidden">
      <img src={src} alt={label} className="max-w-full max-h-full object-contain [image-rendering:pixelated]" loading="lazy" />
    </div>
    <span className="text-xs text-slate-400 font-mono">{label}</span>
  </div>
);

const EslDemoGodot = () => {
  useDocMeta(
    'BLOX ESL — Demo de juego en Godot',
    'Demo BLOX ESL integrada en Godot 4.6 vía GDExtension: player.blox controla movimiento, ataque, enemigos y transición entre niveles mientras el motor conserva render, audio y escena.'
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />

      <section className="border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <DemoHero
            eyebrow={<><PlayCircle className="w-3 h-3" /> Demo 1 de 3</>}
            title="Demo de juego — Godot"
            subtitle="Godot 4.6 + GDExtension + blox_core.dll. La demo carga player.blox, ejecuta FUNCION PRINCIPAL, llama update(dt, entrada) por frame y aplica la TABLA devuelta por BLOX."
            accent="cyan"
          />
        </div>
      </section>

      {/* ESCENARIO */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl overflow-hidden border border-slate-700/60 mb-8">
            <img
              src="/esl/levels/fondo_nivel_1.png"
              alt="Nivel 1 de la demo"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>

          <h3 className="text-xl font-semibold text-cyan-400 mb-4">Assets de la demo</h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            <AssetPill src="/esl/player_front.png" label="jugador" />
            <AssetPill src="/esl/player_right_1.png" label="jugador →" />
            <AssetPill src="/esl/enemy_front.png" label="enemigo" />
            <AssetPill src="/esl/gargolas/gargola_petrificada.png" label="gárgola" />
            <AssetPill src="/esl/gargolas/gargola_viva_sentada.png" label="gárgola viva" />
            <AssetPill src="/esl/pocion.png" label="poción" />
            <AssetPill src="/esl/gems/gem_yellow_on.png" label="gema amarilla" />
            <AssetPill src="/esl/gems/gem_cyan_on.png" label="gema cian" />
            <AssetPill src="/esl/gems/gem_red_on.png" label="gema roja" />
            <AssetPill src="/esl/gems/gem_purple_on.png" label="gema púrpura" />
            <AssetPill src="/esl/gems/gem_black_on.png" label="gema negra" />
            <AssetPill src="/esl/lever_open.png" label="palanca" />
            <AssetPill src="/esl/puerta_cerrada.png" label="puerta" />
            <AssetPill src="/esl/effects/magia_oscura.png" label="magia" />
            <AssetPill src="/esl/effects/veneno_gargola.png" label="veneno" />
            <AssetPill src="/esl/Recuadro_de_fin_de_juego.png" label="game over" />
          </div>
        </div>
      </section>

      {/* NIVELES */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">Juego demo</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 font-bold flex items-center justify-center">1</div>
                <h3 className="text-2xl font-semibold text-white">Nivel 1</h3>
              </div>
              <ul className="text-slate-300 space-y-2 text-sm leading-relaxed">
                <li>• Poción sobre un pilar que otorga magia al jugador.</li>
                <li>• Gárgolas que despiertan cerca del jugador y disparan veneno.</li>
                <li>• Disparo del jugador cuando tiene magia; impacta y las devuelve a piedra.</li>
                <li>• Transición al nivel 2 al derrotar todas las gárgolas y avanzar al borde.</li>
              </ul>
            </div>
            <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-400 font-bold flex items-center justify-center">2</div>
                <h3 className="text-2xl font-semibold text-white">Nivel 2</h3>
              </div>
              <ul className="text-slate-300 space-y-2 text-sm leading-relaxed">
                <li>• Gemas coloridas (amarilla, cian, roja, púrpura) para recoger.</li>
                <li>• Gemas negras opcionales que despiertan una amenaza adicional y activan el segundo enemigo.</li>
                <li>• Palanca que abre la puerta al recoger todas las gemas requeridas.</li>
                <li>• Enemigos que persiguen al jugador con lógica escrita en BLOX.</li>
                <li>• Victoria al cruzar la puerta abierta.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* API */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">API y snippets</h2>
          <p className="text-slate-400 text-lg mb-8">
            Ejemplos reales tomados de <code className="text-cyan-400 font-mono">main.gd</code> y <code className="text-cyan-400 font-mono">player.blox</code>.
          </p>

          <div className="mb-6">
            <h3 className="text-xl font-semibold text-cyan-400 mb-2">1. Godot carga BLOX y llama <code className="font-mono">update()</code></h3>
            <CodeBox filename="main.gd" lang="gdscript">{`var blox := BloxRuntime.new()

if not blox.is_ready():
    push_error(blox.get_last_error())
    return

var script_path := ProjectSettings.globalize_path("res://scripts/player.blox")
blox.load_file(script_path)
blox.run_main()
blox.call_function("init")

# En el _process(delta):
blox.set_host_state(make_host_state())
var state = blox.call_function(
    "update",
    [delta, make_input_state(mode, dir.x, dir.y, attack)]
)
apply_entity_states(state)`}</CodeBox>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-semibold text-cyan-400 mb-2">2. BLOX enruta <code className="font-mono">update()</code> entre niveles</h3>
            <CodeBox filename="player.blox" lang="blox">{`FUNCION update(dt, entrada)
INICIO
    TABLA estado_juego_iniciado, estado_nivel_actual
    NUMERICO nivel_actual
    LOGICO juego_iniciado

    estado_juego_iniciado = GODOT_ESTADO("juego_iniciado")
    estado_nivel_actual   = GODOT_ESTADO("nivel_actual")

    nivel_actual = 1
    juego_iniciado = estado_juego_iniciado[1].logico

    SI (juego_iniciado == VERDADERO) ENTONCES
        nivel_actual = estado_nivel_actual[1].numero
    FIN_SI

    SI (nivel_actual == 1) ENTONCES
        RETORNAR update_nivel_1(dt, entrada)
    FIN_SI

    SI (nivel_actual == 2) ENTONCES
        RETORNAR update_nivel_2(dt, entrada)
    FIN_SI

    RETORNAR update_nivel_1(dt, entrada)
FINAL_FUNCION`}</CodeBox>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-semibold text-cyan-400 mb-2">3. BLOX devuelve estados al host</h3>
            <CodeBox filename="player.blox" lang="blox">{`AGREGAR(estados, {id = "__sonido",  sonido = "player_hit"})

SI (jugador[1].vida < 1) ENTONCES
    game_over = VERDADERO
    AGREGAR(estados, {id = "__sonido",  sonido = "game_over"})
    AGREGAR(estados, {id = "__mensaje", texto = "Sin vidas.", visible = VERDADERO})
FIN_SI`}</CodeBox>
            <p className="text-slate-400 text-sm">BLOX no reproduce audio ni dibuja: pide al host que lo haga. El host aplica el cambio en su propio motor.</p>
          </div>

          <div className="rounded-2xl bg-slate-800/40 border border-slate-700/60 p-6 mt-10">
            <h3 className="text-xl font-semibold text-white mb-4">Superficie API pública</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
              {[
                'BloxVM', 'BloxRuntime', 'blox_vm_create()', 'blox_vm_load_file()',
                'blox_vm_run_main()', 'blox_vm_call()', 'blox_vm_register_native()', 'load_file()',
                'run_main()', 'call_function()', 'set_host_state()', 'get_last_error()',
                'GODOT_CREAR_ENTIDAD()', 'GODOT_ESTADO()', 'TABLA', 'BloxValue',
              ].map(x => (
                <code key={x} className="px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700/50 text-cyan-400 font-mono text-xs truncate">
                  {x}
                </code>
              ))}
            </div>
          </div>

          <Callout tone="emerald" label="Resumen">
            El motor Godot mantiene render, audio y escena. Toda la lógica de gameplay
            (movimiento, disparo, transición de nivel, victoria/derrota) vive en <code className="font-mono text-emerald-300">player.blox</code>
            y se puede editar sin recompilar la extensión C.
          </Callout>
        </div>
      </section>

      {/* CTA cross-demo */}
      <section className="pt-16 pb-32 sm:pb-24 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Boxes className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Explora las otras integraciones
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/embedded-scripting-language/demo-industrial"
              data-testid="godot-to-industrial"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              <Cpu className="w-4 h-4" />
              Demo industrial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/embedded-scripting-language/demo-negocios"
              data-testid="godot-to-negocios"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              Demo de negocios
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EslDemoGodot;
